// "Watch the class" — scenario pages that have a produced classroom episode
// embed it here: YouTube first (monetizable) with self-hosted fallback, and
// the full transcript as a collapsed section so the text is on the page
// (SEO + accessibility) without dominating it.
import { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, FileText, ChevronRight } from "lucide-react";
import { useClassroomManifest, displayTitle } from "@/pages/Classroom";
import { extractYouTubeId } from "@/components/classroom/YouTubeLessonPlayer";

const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

export function ScenarioClassVideo({ scenarioId, dark }: { scenarioId: string; dark: boolean }) {
  const { data } = useClassroomManifest();
  const [open, setOpen] = useState(false);

  const lesson = data?.lessons?.find(
    (l) =>
      l.status === "published" &&
      (((l as any).scenarioIds ?? []).includes(scenarioId) || (l as any).scenarioId === scenarioId),
  );
  if (!lesson) return null;

  const ytId = extractYouTubeId(lesson.youtubeUrl);
  const ink = dark ? "#e9eef6" : "#241f18";
  const mut = dark ? "#9fb0c8" : "#6b6250";
  const card = dark ? "var(--civic-navy-deeper)" : "#fff";
  const line = dark ? "var(--civic-navy-deep)" : "#ddd5c4";
  const navy = dark ? "#e9eef6" : "var(--civic-navy-deep)";

  return (
    <section style={{ margin: "26px 0", background: card, border: `1px solid ${line}`, borderRadius: 14, padding: "18px 18px 16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <GraduationCap size={20} color="var(--civic-gold)" style={{ alignSelf: "center" }} />
        <h2 style={{ fontFamily: serif, fontSize: 22, margin: 0, color: navy }}>
          Watch the class: {displayTitle(lesson)}
        </h2>
        <Link to={`/classroom/${lesson.slug}`} style={{ marginLeft: "auto", fontFamily: sans, fontSize: 13.5, color: mut, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 2 }}>
          Open in the Classroom <ChevronRight size={14} />
        </Link>
      </div>

      {ytId ? (
        <div style={{ position: "relative", aspectRatio: "16/9", borderRadius: 10, overflow: "hidden", background: "#000" }}>
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?rel=0&vq=hd1080&cc_load_policy=3`}
            title={displayTitle(lesson)}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          />
        </div>
      ) : (
        <video
          controls
          playsInline
          preload="metadata"
          poster={lesson.posterUrl ?? undefined}
          src={lesson.videoUrl ?? undefined}
          style={{ width: "100%", borderRadius: 10, background: "#000", display: "block", aspectRatio: "16/9" }}
        />
      )}

      {lesson.transcript.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={() => setOpen(!open)}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: sans, fontWeight: 700, fontSize: 14, color: navy, background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
          >
            <FileText size={15} /> Transcript {open ? "▾" : "▸"}
          </button>
          <div style={{ display: open ? "block" : "none", marginTop: 10, maxHeight: 420, overflowY: "auto", paddingRight: 8 }}>
            {lesson.transcript.map((l) => (
              <p key={l.n} style={{ fontFamily: sans, fontSize: 14.5, lineHeight: 1.7, color: ink, margin: "0 0 12px" }}>{l.text}</p>
            ))}
          </div>
          {/* full text stays crawlable while collapsed */}
          {!open && (
            <div style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clipPath: "inset(50%)" }}>
              {lesson.transcript.map((l) => <p key={l.n}>{l.text}</p>)}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
